import React, { useState } from "react";

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Stack,
  Link,
  CircularProgress,
} from "@mui/material";
import Modal, { CommonModalProps } from "..";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DbDeliveryBroadcasterInfos } from "../../../Core/Api/Types/db-delivery-broadcaster-infos";
import { DbCultureInfos } from "../../../Core/Api/Types/db-culture-infos";
import { useAppSelector } from "../../../Redux/redux-store";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import FormInputSelect from "../../Forms/FormInputSelect/form-input-select";
import { assetService, cacheService } from "../../../Core/Api/services";
import { TitleType } from "../../../Core/Api/Types/title-type";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { ObjectType } from "../../../Core/Api/Types/object-type";

import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { BaseMetadataType } from "../../../Core/Api/Types/base-metadata-type";
import { IDbBaseMetadataValueInfos } from "../../../Core/Api/Types/i-db-base-metadata-value-infos";
import { CreateNewDbMetadataValue } from "../../../Core/Utils/asset-object-utils";
import { getGeneratedName } from "../../../Redux/Utils/Bundlemaker/bundlemaker-utils";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import useSelectValidator from "../../../Utils/Hooks/Validators/useSelectValidator";
import { SetBundleId } from "../../../Redux/Reducers/redux-slice-bundle-maker";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export type onCloseCreatePackageModalProps = {
  createdObjects: DbAssetObjectInfos[] | [];
  selectedCultures: DbCultureInfos[];
  broadcasterId: number;
};

export type ModalPackageCreateOrEditElementProps = {
  title: DbAssetObjectInfos | undefined;
  titleId;
  broadcasters: DbDeliveryBroadcasterInfos[];
  cultures: DbCultureInfos[];
  bundleId: number | undefined;
  titleType: TitleType | undefined;
  nbEpisode: number | undefined;
  onClose?: (props: onCloseCreatePackageModalProps) => void;
};

export type ModalPackageCreateOrEditProps = CommonModalProps<ModalPackageCreateOrEditElementProps>;

const ModalCreatePackage = React.forwardRef<any, ModalPackageCreateOrEditProps>((props, ref) => {
  const { t } = useTranslation();
  const { titleId, title, broadcasters, cultures, bundleId, titleType, nbEpisode, closeModal, onClose } = props;

  const enqueueCustomSnackbar = useCustomSnackbar();
  const dispatch = useDispatch();

  //------Local state------

  const [selectedBroadcasterId, setSelectedBroadcasterId] = useState<number>(-1);
  const [selectedCultures, setSelectedCultures] = useState<string[]>([]);
  const [selectedEpisodes, setSelectedEpisodes] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  //------Form validation------

  const formId = "package-modal";
  const formValid = useAppSelector(GetFormIsValid(formId));
  const validateSelectEpisode = useSelectValidator<any>({
    formId: formId,
    fieldId: "episodes",
    required: titleType === "Season",
  });

  //------Handle user actions------

  const handleBroadcasterChange = (value: any) => {
    setSelectedBroadcasterId(value as number);
  };

  const handleEpisodeChange = (event) => {
    validateSelectEpisode(event.target.value.length === 0 ? null : event.target.value);
    const {
      target: { value },
    } = event;
    setSelectedEpisodes(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleSave = () => {
    setIsSaving(true);
    if (bundleId && bundleId > 0) {
      createPackage(bundleId);
    } else {
      createBundle();
    }
  };

  //------Data generation------

  const generateBroacasterIdMetadata = () => {
    const metadataId = cacheService.GetMetadata().find((x) => x.tagName === "broadcasterId")?.id;
    if (!metadataId) return;
    let meta = CreateNewDbMetadataValue(
      metadataId,
      BaseMetadataType.DbLongMetadataValueInfos,
      "broadcasterId",
      selectedBroadcasterId
    );

    meta.fieldName = "broadcasterId";
    return meta;
  };

  const generatePackageObject = (episodeNb: number | null = null, parentId: number) =>
    new DbAssetObjectInfos({
      isNew: true,
      hidden: false,
      isDeleted: false,
      type: ObjectType.BundlePackage,
      objectType: ObjectType.BundlePackage,
      layer: ObjectLayer.Asset,
      status: ObjectStatus.New,
      name: getGeneratedName(
        cultures.filter((x) => selectedCultures.find((y) => x.englishName === y)),
        episodeNb ?? undefined
      ),
      parentObjectId: parentId,
      parentTitleId: titleId,
      classification: ObjectClass.Project,
      allParentObjectIds: [parentId, titleId],
      metadata: generateMetadata(episodeNb),
      parentTitleName: title?.name ?? null,
      parentTitle: title ?? null,
      trash: false,
    });

  const generateEpisodeMetadata = (episodeNb: number) => {
    const episodeNbMetadataId = cacheService
      .GetMetadata()
      .find((x) => x.tagName === "serieEpisodeNumberProduction")?.id;
    if (!episodeNbMetadataId) return;
    return CreateNewDbMetadataValue(
      episodeNbMetadataId,
      BaseMetadataType.DbLongMetadataValueInfos,
      "serieEpisodeNumberProduction",
      episodeNb
    );
  };

  const generateBundle = () =>
    new DbAssetObjectInfos({
      isNew: true,
      hidden: false,
      isDeleted: false,
      type: ObjectType.GraphSkeleton,
      objectType: ObjectType.GraphSkeleton,
      layer: ObjectLayer.Content,
      status: ObjectStatus.New,
      name: "Bundles",
      metadata: [
        CreateNewDbMetadataValue(
          cacheService.GetMetadata().find((x) => x.tagName === "contentType")!.id,
          BaseMetadataType.DbStringMetadataValueInfos,
          "contentType",
          "Bundles"
        ),
      ],
      parentObjectId: titleId,
      parentTitleId: titleId,
      classification: ObjectClass.Group,
      allParentObjectIds: [titleId],
      trash: false,
    });

  const generateMetadata = (episodeNb: number | null) => {
    const metadata: IDbBaseMetadataValueInfos[] = [];
    const broadcasterIdMetadata = generateBroacasterIdMetadata();
    if (!broadcasterIdMetadata) return;

    metadata.push(broadcasterIdMetadata);

    if (episodeNb && episodeNb > 0) {
      const episodeNbMetadata = generateEpisodeMetadata(episodeNb);
      episodeNbMetadata && metadata.push(episodeNbMetadata);
    }

    return metadata;
  };

  //------Requests------

  const createBundle = () => {
    assetService.CreateStandalone({ objs: [generateBundle()] }).then((response) => {
      if (
        response &&
        response.success &&
        response.createdObjects &&
        response.createdObjects[0] &&
        response.createdObjects[0].id
      ) {
        createPackage(response.createdObjects[0].id);
        dispatch(SetBundleId(response.createdObjects[0].id));
      } else {
        setIsSaving(false);
        enqueueCustomSnackbar(response.message ?? t("alert:error.creatingBundleContent"), "error");
      }
    });
  };

  const createPackage = (parentId: number) => {
    let data: DbAssetObjectInfos[] = [];

    if (selectedEpisodes && selectedEpisodes.length)
      data = selectedEpisodes.map((x) => generatePackageObject(x, parentId));
    else data.push(generatePackageObject(null, parentId));

    assetService.CreateStandalone({ objs: data }).then((response) => {
      setIsSaving(false);
      if (response.success) {
        onClose &&
          onClose({
            broadcasterId: selectedBroadcasterId,
            createdObjects: response.createdObjects ?? [],
            selectedCultures: cultures.filter((x) => selectedCultures.find((y) => x.englishName === y)),
          });

        closeModal();
      } else {
        enqueueCustomSnackbar(response.message ?? t("alert:error.creatingPackage"), "error");
      }
    });
  };

  return (
    <Modal
      closeModal={closeModal}
      reference={ref}
      width={750}
      title={t("modal:title.createPackage")}
      footer={
        <Stack position="relative" direction="row" spacing={2}>
          <Button onClick={() => closeModal()} startIcon={<ArrowBackIcon />} color="inherit">
            {t("common:cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSave()}
            disabled={!formValid || isSaving}
            startIcon={<AddIcon />}
            sx={{ flex: 1 }}
          >
            {t("common:create")}
          </Button>
          {isSaving && (
            <CircularProgress
              size={24}
              sx={{
                position: "absolute",
                top: "8px",
                right: "12px",
              }}
            />
          )}
        </Stack>
      }
    >
      <InputLabel htmlFor="select-broadcaster">{t("field:label.selectBroadcaster")}</InputLabel>
      <Autocomplete
        sx={{ marginTop: "20px", marginBottom: "20px" }}
        value={broadcasters.find((x) => x.id === selectedBroadcasterId) ?? undefined}
        onChange={(e, newValue) => newValue && handleBroadcasterChange(newValue.id)}
        id="select-broadcaster"
        options={[...broadcasters].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))}
        getOptionLabel={(option) => option?.name ?? ""}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        filterSelectedOptions
        fullWidth
        renderInput={(params) => (
          <OutlinedInput
            rows={1}
            {...params.InputProps}
            inputProps={params.inputProps}
            required
            sx={{ width: "100%", overflowY: "scroll", maxHeight: "50px" }}
            placeholder={t("field:label.selectBroadcaster")}
          />
        )}
      />

      <InputLabel htmlFor="select-cultures">{t("field:label.selectCountry")}</InputLabel>
      <Autocomplete
        sx={{ marginTop: "20px" }}
        value={selectedCultures}
        onChange={(e, newValue) => setSelectedCultures(newValue as string[])}
        multiple
        id="select-cultures"
        options={[...cultures]
          .sort((a, b) => (a.englishName ?? "").localeCompare(b.englishName ?? ""))
          .map((x) => x.englishName)}
        getOptionLabel={(option) => option ?? ""}
        defaultValue={[]}
        filterSelectedOptions
        fullWidth
        renderInput={(params) => (
          <OutlinedInput
            rows={1}
            {...params.InputProps}
            inputProps={params.inputProps}
            required
            sx={{ width: "100%", overflowY: "scroll", maxHeight: "50px" }}
            placeholder={t("field:placeholder.selectCountries")}
          />
        )}
      />
      {selectedCultures.length === 0 && (
        <FormHelperText sx={{ marginTop: "20px" }} error>
          {t("field:helper.fieldIsRequired")}
        </FormHelperText>
      )}
      {titleType === "Season" && nbEpisode && nbEpisode > 1 && (
        <>
          <FormInputSelect
            label={t("field:label.selectEpisodes")}
            stackProps={{
              direction: "column",
              justifyContent: "space-between",
              spacing: 2,
              marginTop: "10px",
            }}
            validationProps={{ formId: formId, fieldId: "episodes", required: true }}
            labelId="demo-multiple-chip-label"
            multiple
            fullWidth
            value={selectedEpisodes}
            onChange={handleEpisodeChange}
            input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selectedEpisodes.map((value) => (
                  <Chip
                    onMouseDown={(event) => {
                      event.stopPropagation();
                    }}
                    key={value}
                    label={value}
                    onDelete={(e) =>
                      handleEpisodeChange({
                        ...e,
                        target: { ...e.target, value: selectedEpisodes.filter((x) => x !== value) },
                      })
                    }
                  />
                ))}
              </Box>
            )}
            MenuProps={MenuProps}
          >
            {[...Array(nbEpisode).keys()].map((x) => (
              <MenuItem key={x} value={x + 1}>
                {t("common:episode")} {x + 1}
              </MenuItem>
            ))}
          </FormInputSelect>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              flexDirection: "row",
              flexWrap: "nowrap",
              marginTop: selectedEpisodes.length === 0 ? "-14px" : "8px",
            }}
          >
            <Link
              underline="hover"
              sx={{ marginRight: "15px" }}
              onClick={(e) => {
                handleEpisodeChange({
                  ...e,
                  target: { ...e.target, value: [...Array(nbEpisode).keys()].map((x) => x + 1) },
                });
              }}
            >
              {t("common:selectAll")}
            </Link>
            <Link underline="hover" onClick={(e) => handleEpisodeChange({ ...e, target: { ...e.target, value: [] } })}>
              {t("common:unselectAll")}
            </Link>
          </Box>
        </>
      )}
    </Modal>
  );
});

export default ModalCreatePackage;
